/**
 * Question types for AutoQM Web Application
 *
 * 28+ question types (문제rows) with difficulty variants
 * 8 reference material types (자료rows)
 * 6 textbook material types (교재rows)
 */

// Question types (문제rows) - 28 types
export const QUESTION_TYPES = [
  '어휘1단계',
  '어휘2단계',
  '어휘3단계',
  '내용일치(한)',
  '내용일치',
  '밑줄의미',
  '함축의미',
  '무관한문장',
  '빈칸추론',
  '추론',
  '추론불가',
  '순서',
  '삽입',
  '목적',
  '주제',
  '제목',
  '요지',
  '연결어',
  '요약',
  '어법1단계',
  '어법2단계',
  '빈칸암기',
  '어휘종합',
  '내용종합',
  '영작1',
  '영작2',
  '주제영작',
  '동형문제',
] as const;

// Materials with per-type generation options share the option storage contract.
export type QuestionType = typeof QUESTION_TYPES[number] | '동반의어' | '단어정리' | '커스텀';

// Reference material types (자료rows)
export const REFERENCE_MATERIAL_TYPES = [
  '동반의어',
  '동반의어문제1',
  '동반의어문제2',
  '영영정의',
  '영영정의문제',
  '요약문',
  '커스텀',
  '음성파일',
] as const;

export type ReferenceMaterialType = typeof REFERENCE_MATERIAL_TYPES[number];

// Textbook material types (교재rows)
// 분석본 is Normal-only (SPEC-BUNSEOKBON-001 REQ-BB-001); textbook types get no
// Easy/Hard toggles because DIFFICULTY_SUPPORTED_TYPES below is QuestionType-only.
export const TEXTBOOK_MATERIAL_TYPES = [
  '지문분석',
  '분석본',
  '한글해석',
  '직독직해',
  '단어정리',
  '한줄해석연습',
  '한줄영작연습',
] as const;

export type TextbookMaterialType = typeof TEXTBOOK_MATERIAL_TYPES[number];

// Question types that support macro functions (F1-F5)
export const MACRO_SUPPORTED_TYPES = [
  '어법1단계',
  '어휘1단계',
  '어휘2단계',
  '어법2단계',
  '어휘3단계',
  '동반의어',
  '한줄해석연습',
  '한줄영작연습',
] as const;

export type MacroSupportedType = typeof MACRO_SUPPORTED_TYPES[number];

export const ALL_GENERATION_TYPES = [
  ...QUESTION_TYPES,
  ...REFERENCE_MATERIAL_TYPES,
  ...TEXTBOOK_MATERIAL_TYPES,
] as const;

export type GenerationType = typeof ALL_GENERATION_TYPES[number];

const GENERATION_TYPE_ORDER = new Map<string, number>(
  ALL_GENERATION_TYPES.map((type, index) => [type, index])
);

/**
 * Compare generation-type names using the web app's canonical selection order.
 * Types outside the current catalog stay after known types while retaining their
 * existing relative order when used with JavaScript's stable Array#sort.
 */
export function compareGenerationTypeOrder(left: string, right: string): number {
  const leftIndex = GENERATION_TYPE_ORDER.get(left);
  const rightIndex = GENERATION_TYPE_ORDER.get(right);

  if (leftIndex == null || rightIndex == null) {
    if (leftIndex == null && rightIndex == null) return 0;
    return leftIndex == null ? 1 : -1;
  }

  return leftIndex - rightIndex;
}

// Difficulty levels
export type DifficultyLevel = 'Easy' | 'Normal' | 'Hard';

// Question types that support difficulty variants (Easy eligibility).
// Easy and Hard eligibility are INDEPENDENT sets — a type may support one,
// both, or neither. Do not derive one list from the other.
export const DIFFICULTY_SUPPORTED_TYPES: QuestionType[] = [
  '어휘1단계',
  '어휘2단계',
  '어휘3단계',
  '내용일치',
  '내용일치(한)',
  '무관한문장',
  '빈칸추론',
  '추론',
  '추론불가',
  '밑줄의미',
  '요약',
  // '빈칸암기' — 2026-08-20 임시 제거. Easy 프롬프트가 Normal과 동일해
  // 실질 차이가 없다. 되살리려면 이 줄의 주석만 해제하면 된다.
  // '어법2단계' — 2026-08-20 임시 제거. 다른 유형과 달리 Easy 전용 프롬프트
  // (Parse-Breaking Gate: 파싱 자체가 불가능한 오류만 정답으로 허용)가 실제로
  // 존재하며 서버에도 그대로 남아 있다. 되살리려면 이 줄의 주석만 해제하면 된다.
];

// Reference-material rows use the same admin Easy/Normal selector, but stay
// outside the question-only bulk difficulty matrix.
export const REFERENCE_DIFFICULTY_SUPPORTED_TYPES: ReferenceMaterialType[] = [
  '요약문',
];

export function supportsEasyDifficulty(type: string): boolean {
  return (
    DIFFICULTY_SUPPORTED_TYPES.includes(type as QuestionType) ||
    REFERENCE_DIFFICULTY_SUPPORTED_TYPES.includes(type as ReferenceMaterialType)
  );
}

// Hard-eligible types — mirrors desktop 문제rows_with_hard_level (autoQM.py).
// 밑줄의미/요약 are absent: their roles pipelines register Normal/Easy only and
// stray _Hard requests are aliased onto Normal server-side.
export const HARD_DIFFICULTY_SUPPORTED_TYPES: QuestionType[] = [
  '어휘1단계',
  '어휘2단계',
  '어휘3단계',
  '내용일치',
  '내용일치(한)',
  '무관한문장',
  '빈칸추론',
  '추론',
  '추론불가',
  '순서',
  '연결어',
  '어법2단계',
  '빈칸암기',
  '영작1',
  '영작2',
  '주제영작',
];

// Question types that have advanced options dialogs (25 types)
// Excludes: 빈칸암기, 어휘종합, 내용종합 (no options per desktop app)
export const OPTIONS_SUPPORTED_TYPES: QuestionType[] = [
  '커스텀',
  '동반의어',
  '단어정리',
  // 어휘 계열
  '어휘1단계',
  '어휘2단계',
  '어휘3단계',
  // 어법 계열
  '어법1단계',
  '어법2단계',
  // 내용일치 계열
  '내용일치',
  '내용일치(한)',
  // 밑줄/무관한문장
  '밑줄의미',
  '함축의미',
  '무관한문장',
  // 빈칸/추론
  '빈칸추론',
  '추론',
  '추론불가',
  // 구조
  '순서',
  '삽입',
  '연결어',
  // 목적/주제/제목/요지
  '목적',
  '주제',
  '제목',
  '요지',
  '요약',
  // 영작
  '영작1',
  '영작2',
  '주제영작',
  // 동형문제
  '동형문제',
];

// ============================================================================
// Mocktest Types (실전모의고사)
// ============================================================================

// 실모rows - 21개 문제 유형 (Desktop 앱 기준)
export const MOCKTEST_TYPES = [
  '어휘3단계',
  '밑줄의미',
  '함축의미',
  '내용일치(한)',
  '내용일치',
  '무관한문장',
  '빈칸추론',
  '추론',
  '추론불가',
  '순서',
  '삽입',
  '목적',
  '주제',
  '제목',
  '요지',
  '요약',
  '어법2단계',
  '영작1',
  '영작2',
  '주제영작',
  '동형문제',
] as const;

export type MocktestType = typeof MOCKTEST_TYPES[number];

// 실모rows_with_hard_level - Hard 난이도 지원 9개 유형
export const MOCKTEST_HARD_SUPPORTED_TYPES: MocktestType[] = [
  '내용일치',
  '내용일치(한)',
  '무관한문장',
  '빈칸추론',
  '추론',
  '추론불가',
  '영작1',
  '영작2',
  '주제영작',
];

// 실모rows Easy 난이도 지원 유형 — Hard 목록과 독립적으로 관리한다.
// 영작1/영작2/주제영작은 Hard만 지원하며 Easy는 제공하지 않는다.
export const MOCKTEST_EASY_SUPPORTED_TYPES: MocktestType[] = [
  '내용일치',
  '내용일치(한)',
  '무관한문장',
  '빈칸추론',
  '추론',
  '추론불가',
];

// 21개 전부 옵션 지원 (데스크톱과 동일하게 확장)
export const MOCKTEST_OPTIONS_SUPPORTED_TYPES: MocktestType[] = [...MOCKTEST_TYPES];

// ============================================================================

// Base question options
export interface BaseQuestionOptions {
  word_count_min?: number;
  word_count_max?: number;
  generation_mode?: number;
  ebs_matching?: boolean;
  custom_prompt?: string;
}

// Specific question options (extend BaseQuestionOptions as needed)
export type 어휘3단계Options = BaseQuestionOptions;

export type 내용일치Options = BaseQuestionOptions;

export type 빈칸추론Options = BaseQuestionOptions;

export type 순서Options = BaseQuestionOptions;

export type 삽입Options = BaseQuestionOptions;

export type 영작Options = BaseQuestionOptions;

// Question generation request
export interface GenerateQuestionData {
  passage: string;
  questionType: QuestionType;
  difficulty: DifficultyLevel;
  options?: BaseQuestionOptions;
}

// Question generation result
export interface QuestionResult {
  passage_id: string;
  passage: string;
  question: string;
  answer: string;
  explanation: string;
  credits_used?: number;
}

// Batch processing
export interface BatchProcessingState {
  total: number;
  completed: number;
  failed: number;
  inProgress: boolean;
  results: QuestionResult[];
  errors: Array<{ passageIndex: number; error: string }>;
}
